import React from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";

const META = {
  terms: {
    title: "Terms of Service",
    eyebrow: "◇ Legal",
    updated: "December 2025",
    intro:
      "These terms govern the use of Ulties.com and any services or open-source materials Ulties Studio Lda. makes available. By accessing the site or engaging us you agree to them. We've kept the language plain — if anything is unclear, write to hello@ulties.com.",
    sections: [
      {
        h: "1. Who we are",
        p: [
          "Ulties is operated by Ulties Studio Lda., a company registered in Lisbon, Portugal (the \"Studio\"). When we say \"we\" or \"us\" we mean the Studio. When we say \"you\" we mean the human or company using the site or engaging our services.",
        ],
      },
      {
        h: "2. Engagement terms",
        p: [
          "Paid client work is governed by a separate Statement of Work (SOW) signed by both parties. Nothing on this website constitutes an offer to be bound by an SOW.",
          "Until an SOW is signed, conversations, briefs and proposals exchanged are confidential but not contractually binding on either side.",
        ],
      },
      {
        h: "3. Acceptable use",
        p: [
          "Don't break the law with our site, don't try to access systems you aren't authorised to, and don't scrape at a rate that disrupts other users. We reserve the right to throttle or block traffic that does any of the above.",
        ],
      },
      {
        h: "4. Intellectual property",
        p: [
          "Site content (copy, design, illustrations) is © Ulties Studio Lda. unless otherwise marked. Open-source code published by the Studio is governed by the license attached to each repository.",
          "Work produced under a signed SOW transfers to the client per the IP terms of that SOW.",
        ],
      },
      {
        h: "5. Warranties and liability",
        p: [
          "We provide the site \"as is\" without warranties of any kind. To the extent permitted by law our aggregate liability for any claim arising from your use of the site is limited to €100.",
          "Liability under paid engagements is governed by the SOW.",
        ],
      },
      {
        h: "6. Changes",
        p: [
          "We may update these terms. Material changes will be announced on this page and dated above.",
        ],
      },
      {
        h: "7. Contact",
        p: [
          "Questions about these terms? Write to legal@ulties.com. We answer within 5 business days.",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "◇ Legal",
    updated: "December 2025",
    intro:
      "We collect the minimum needed to run the site, respond to enquiries, and run the studio. We don't sell data. We don't run third-party advertising. If you'd rather we delete what we have on you, write to privacy@ulties.com.",
    sections: [
      {
        h: "1. What we collect",
        p: [
          "When you fill in a contact form: your name, email, company, an optional budget range, and your message.",
          "When you browse: anonymous, aggregated analytics (page views, country, browser) if you accepted analytics cookies.",
          "When you sponsor us: billing details handled by our payment processor (we never see your card number).",
        ],
      },
      {
        h: "2. Why we collect it",
        p: [
          "Contact-form data is used solely to reply to your enquiry and, if you become a client, to deliver the engagement.",
          "Analytics is used to understand which pages are useful so we can improve them.",
          "Sponsorship data is used to invoice you and honour the perks of your tier.",
        ],
      },
      {
        h: "3. How long we keep it",
        p: [
          "Contact-form data: 24 months from last contact, then deleted.",
          "Analytics: 14 months, then aggregated and anonymised.",
          "Sponsorship records: 10 years (statutory requirement).",
        ],
      },
      {
        h: "4. Who we share it with",
        p: [
          "Hosting (Vercel, AWS), error monitoring (Sentry), email (Resend), payments (Stripe). Each is bound by a DPA. We do not share data with marketing or advertising platforms.",
        ],
      },
      {
        h: "5. Cookies",
        p: [
          "Strictly-necessary cookies keep the site working. Analytics cookies are optional and only set if you accept them via the banner. You can revisit your choice from the cookie preferences link at any time.",
        ],
      },
      {
        h: "6. Your rights",
        p: [
          "Under GDPR (and equivalent regimes) you can request access, correction, deletion, or portability of your data. Write to privacy@ulties.com and we will respond within 30 days.",
        ],
      },
      {
        h: "7. Contact",
        p: ["Questions? privacy@ulties.com."],
      },
    ],
  },
};

function LegalLayout({ doc, slug }) {
  return (
    <PageShell>
      <article className="pt-28 md:pt-36 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x flex items-center gap-3">
            <span>{doc.eyebrow}</span>
            <span className="h-px w-8 bg-hairline-strong" />
            <span>Last updated · {doc.updated}</span>
          </div>
          <h1
            data-testid={`legal-title-${slug}`}
            className="mt-6 font-display text-5xl md:text-7xl font-bold tracking-[-0.04em] leading-[0.95] text-primary-x"
          >
            {doc.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-secondary-x leading-relaxed">
            {doc.intro}
          </p>

          <div className="mt-16 space-y-12">
            {doc.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-primary-x">
                  {s.h}
                </h2>
                <div className="mt-5 space-y-5 text-secondary-x leading-relaxed">
                  {s.p.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-20 pt-10 border-t border-hairline flex items-center justify-between text-sm">
            <Link to="/terms" className="text-secondary-x hover:text-primary-x transition-colors">
              Terms
            </Link>
            <Link to="/privacy" className="text-secondary-x hover:text-primary-x transition-colors">
              Privacy
            </Link>
            <Link to="/" className="text-secondary-x hover:text-primary-x transition-colors">
              Home
            </Link>
          </div>
        </div>
      </article>
    </PageShell>
  );
}

export function TermsPage() {
  return <LegalLayout doc={META.terms} slug="terms" />;
}

export function PrivacyPage() {
  return <LegalLayout doc={META.privacy} slug="privacy" />;
}
